<template>
  <div>
    <el-header>{{ $t('common.finish') }}</el-header>
    <div style="width:40%;float:left">
      <el-form-item :label="$t('interiorDecorator.width')">
        <input-number
          v-model="romanBlind.widthInCm"
          :precision="1"
          :step="0.5"
          :min="minWidth"
          :max="maxWidth"
          :disabled="loading"
          @change="handleWidthChange"
        />
      </el-form-item>
      <el-form-item :label="$t('interiorDecorator.height')">
        <input-number
          v-model="romanBlind.heightInCm"
          :precision="1"
          :step="0.5"
          :min="minHeight"
          :max="maxHeight"
          :disabled="loading"
          @change="handleHeightChange"
        />
      </el-form-item>
      <el-form-item :label="$t('interiorDecorator.assemblyHeight')">
        <input-number v-model="romanBlind.assemblyHeightInCm" :precision="0" :step="1" />
      </el-form-item>
    </div>
    <div style="width:60%;float:left">
      <el-form-item :label="$t('interiorDecorator.romanBlindSubtype')">
        <el-radio-group v-model="romanBlind.subtype">
          <el-radio v-for="item in subtypes" :key="item.value" :label="item.value">{{ item.label }}</el-radio>
        </el-radio-group>
      </el-form-item>
      <div v-if="!isFabricOnly()">
        <el-form-item :label="$t('interiorDecorator.operatingType')">
          <el-radio-group v-model="romanBlind.operatingType">
            <el-radio v-for="item in operatingTypes" :key="item.value" :label="item.value">
              {{ $t(item.label) }}
            </el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item :label="$t('interiorDecorator.system')">
          <el-radio-group v-model="romanBlind.systemColor">
            <el-radio v-for="item in systemColors" :key="item.value" :label="item.value">
              {{ $t(item.label) }}
            </el-radio>
          </el-radio-group>
        </el-form-item>
        <div v-if="isManuallyOperated()">
          <el-form-item :label="$t('interiorDecorator.chain')">
            <el-radio-group v-model="romanBlind.chainColor">
              <el-radio v-for="item in chainColors" :key="item.value" :label="item.value">
                {{ $t(item.label) }}
              </el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item :label="$t('interiorDecorator.operatingSide')">
            <el-radio-group v-model="romanBlind.operatingSide">
              <el-radio v-for="item in operatingSides" :key="item.value" :label="item.value">
                {{ $t(item.label) }}
              </el-radio>
            </el-radio-group>
          </el-form-item>
        </div>
      </div>
      <el-form-item :label="$t('interiorDecorator.ribs')">
        <el-radio-group v-model="romanBlind.ribs" :disabled="loading" @change="handleRibsChange">
          <el-radio v-for="item in ribs" :key="item.value" :label="item.value">
            {{ $t(item.label) }}
          </el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item :label="$t('interiorDecorator.set')">
        <el-radio-group v-model="romanBlind.ribsEqual">
          <el-radio v-for="item in ribsEqualOptions" :key="item.value" :label="item.value">
            {{ $t(item.label) }}
          </el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item :label="$t('interiorDecorator.cutSize')">
        {{ romanBlind.cutSizeInCm || $t('draftTool.tbd') }}
      </el-form-item>
      <el-form-item :label="$t('orderLine.totalFabricInCm')">
        {{ orderLineDraft.requiredFabricInCm || $t('draftTool.tbd') }}
      </el-form-item>
      <!-- These fields must not be visible, but should be left for easy debugging.
      <el-form-item label="Voering banen links">
        {{ romanBlind.liningPanels || '$t('draftTool.tbd').' }}
      </el-form-item>
      <el-form-item label="Voering knipmaat">
        {{ romanBlind.liningCutSizeInCm || '$t('draftTool.tbd').' }}
      </el-form-item>
      -->
    </div>
  </div>
</template>

<script>
import InputNumber from '@/components/DSE/InputNumber';
import {
  OperatingSide,
  OperatingType,
  RibsEqual,
  RibType,
  RomanBlindChainColor,
  RomanBlindRibs,
  RomanBlindSideHemCorrectionInCm,
  RomanBlindSubtype,
  RomanBlindSystemColor
} from '../constants';

const MIN_WIDTH = 40;
const MAX_WIDTH = 400;
const MIN_HEIGTH = 40;
const MAX_HEIGHT = 400;
const debounce = require('lodash.debounce');

export default {
  components: {
    InputNumber
  },
  props: {
    calculateOrderToggle: {
      type: Boolean
    },
    orderLineDraft: {
      type: Object
    },
    romanBlind: {
      type: Object
    },
    settings: {
      type: Object
    }
  },
  data() {
    return {
      loading: false,
      chainColors: [
        { value: RomanBlindChainColor.White, label: 'draftTool.white' },
        { value: RomanBlindChainColor.Black, label: 'draftTool.black' },
        { value: RomanBlindChainColor.Chrome, label: 'draftTool.chrome' }
      ],
      operatingSides: [
        { value: OperatingSide.Left, label: 'draftTool.left' },
        { value: OperatingSide.Right, label: 'draftTool.right' }
      ],
      operatingTypes: [
        { value: OperatingType.Manual, label: 'draftTool.manual' },
        { value: OperatingType.Electrical, label: 'draftTool.electric' }
      ],
      systemColors: [
        { value: RomanBlindSystemColor.White, label: 'draftTool.white' },
        { value: RomanBlindSystemColor.Black, label: 'draftTool.black' }
      ],
      ribsEqualOptions: [
        { value: RibsEqual.NoSet, label: 'draftTool.setNone' },
        { value: RibsEqual.SetTop, label: 'draftTool.setFromTop' },
        { value: RibsEqual.SetBottom, label: 'draftTool.setFromBottom' }
      ],
      ribs: [
        { value: RomanBlindRibs.TunnelBack, label: 'draftTool.tunnelBack' },
        { value: RomanBlindRibs.TunnelFront, label: 'draftTool.tunnelFront' },
        { value: RomanBlindRibs.Band, label: 'draftTool.tape' }
      ]
    };
  },
  computed: {
    maxHeight() {
      const settings = this.settings;
      if (!settings) {
        return MAX_HEIGHT;
      }
      const maxHeight = this.isLined() ? settings.maximumLinedHeightInCm : settings.maximumHeightInCm;
      return maxHeight > 0 ? maxHeight : MAX_HEIGHT;
    },
    minHeight() {
      const minHeight = (this.settings && this.settings.minimumHeightInCm) || 0;
      return minHeight > 0 ? minHeight : MIN_HEIGTH;
    },
    maxWidth() {
      const settings = this.settings;
      if (!settings) {
        return MAX_WIDTH;
      }
      const maxWidth = this.isLined() ? settings.maximumLinedWidthInCm : settings.maximumWidthInCm;
      return maxWidth > 0 ? maxWidth : MAX_WIDTH;
    },
    minWidth() {
      const minWidth = (this.settings && this.settings.minimumWidthInCm) || 0;
      return minWidth > 0 ? minWidth : MIN_WIDTH;
    },
    subtypes() {
      const operatingType = this.romanBlind.operatingType;
      if (operatingType === OperatingType.Manual) {
        return [
          { value: RomanBlindSubtype.Roman2, label: 'Roman 2' },
          { value: RomanBlindSubtype.Roman2WithoutRail, label: 'Roman 2 excl. rails' },
          { value: RomanBlindSubtype.Roman1, label: 'Roman 1' }
        ];
      } else {
        return [
          { value: RomanBlindSubtype.Roman2, label: 'Roman 2' },
          { value: RomanBlindSubtype.Roman1, label: 'Roman 1' }
        ];
      }
    }
  },
  watch: {
    calculateOrderToggle() {
      this.debouncedCalculateOrder();
    }
  },
  created() {
    this.debouncedCalculateOrder = debounce(this.calculateOrder, 1000);
  },
  methods: {
    calculateOrder() {
      const romanBlind = this.romanBlind;
      const command = {
        widthInMm: romanBlind.widthInCm * 10,
        heightInMm: romanBlind.heightInCm * 10,
        fabric: null,
        lining: null,
        alignPattern: true,
        ribType: this.getRibType(),
        sideFinishCorrectionInMm: RomanBlindSideHemCorrectionInCm * 10
      };

      const orderLineDraft = this.orderLineDraft;
      if (orderLineDraft.fabricProductID) {
        command.fabric = {
          type: orderLineDraft.fabricType,
          widthInMm: orderLineDraft.fabricWidthInCm * 10,
          isPatterned: orderLineDraft.fabricIsPatterned,
          patternHeightInMm: orderLineDraft.fabricPatternHeightInMm
        };
      }
      if (orderLineDraft.liningProductID) {
        command.lining = {
          type: orderLineDraft.liningFabricType,
          widthInMm: orderLineDraft.liningWidthInCm * 10
        };
      }

      if (this.isValidCommand(command)) {
        this.loading = true;
        this.$store
          .dispatch('interiorDecoratorOrder/calculateRomanBlind', command)
          .then(data => {
            romanBlind.cutSizeInCm = data.cutSizeInCm;
            romanBlind.panels = data.numberOfPanels;
            if (this.isLined()) {
              romanBlind.liningPanels = data.numberOfPanelsLining;
              romanBlind.liningCutSizeInCm = data.cutSizeLiningInCm;
            }
            this.orderLineDraft.requiredFabricInCm = (data.numberOfPanels || 1) * data.cutSizeInCm;
          })
          .finally(() => {
            this.loading = false;
          });
      } else {
        romanBlind.cutSizeInCm = null;
        romanBlind.numberOfPanels = null;
        this.orderLineDraft.requiredFabricInCm = null;
      }
    },
    getRibType() {
      switch (this.romanBlind.ribs) {
        case RomanBlindRibs.TunnelBack:
        case RomanBlindRibs.TunnelFront:
          return RibType.Tunnel;
        case RomanBlindRibs.Band:
          return RibType.Band;
        default:
          return undefined;
      }
    },
    handleWidthChange() {
      this.debouncedCalculateOrder();
    },
    handleHeightChange() {
      this.debouncedCalculateOrder();
    },
    handleRibsChange() {
      this.debouncedCalculateOrder();
    },
    isLined() {
      return this.orderLineDraft && this.orderLineDraft.liningProductID;
    },
    isValidCommand(command) {
      return (
        command &&
        command.widthInMm &&
        command.heightInMm &&
        command.ribType !== null &&
        command.ribType !== undefined &&
        command.fabric &&
        command.fabric.widthInMm
      );
    },
    isElectricallyOperated() {
      return this.romanBlind.operatingType === OperatingType.Electrical;
    },
    isFabricOnly() {
      return this.romanBlind.subtype === RomanBlindSubtype.Roman2WithoutRail;
    },
    isManuallyOperated() {
      return this.romanBlind.operatingType === OperatingType.Manual;
    }
  }
};
</script>
